import { useRef, useEffect, useMemo } from 'react';
import * as THREE from 'three';

export function useHighlightMesh(scene: THREE.Object3D | null) {
	const highlightMeshRef = useRef<THREE.Mesh | null>(null);
	const geometry = useMemo(() => new THREE.BoxGeometry(1, 1, 1), []);
	const material = useMemo(() => {
		return new THREE.MeshStandardMaterial({
			color: '#0f8c3b', // Green
			transparent: true,
			opacity: 0.75,
			emissive: '#0f8c3b', // Green glow effect
			emissiveIntensity: 0.8, // Adjust glow strength
			polygonOffset: true,
			polygonOffsetFactor: -1,
			polygonOffsetUnits: -4,
		});
	}, []);
	useEffect(() => {
		if (!scene) return;
		const highlightMesh = new THREE.Mesh(geometry, material);
		highlightMesh.visible = false;
		scene.add(highlightMesh);
		highlightMesh.frustumCulled = false;
		highlightMeshRef.current = highlightMesh;

		return () => {
			scene.remove(highlightMesh);
			highlightMesh.geometry.dispose();
			if (Array.isArray(highlightMesh.material)) {
				highlightMesh.material.forEach((mat) => mat.dispose());
			} else {
				highlightMesh.material.dispose();
			}
		};
	}, [scene]);

	const setHighlightMatrix = (matrix: THREE.Matrix4 | null) => {
		if (!highlightMeshRef.current) return;
		if (matrix) {
			highlightMeshRef.current.matrixAutoUpdate = false;
			highlightMeshRef.current.matrix.copy(matrix);

			const pos = new THREE.Vector3();
			const rot = new THREE.Quaternion();
			const scl = new THREE.Vector3();
			highlightMeshRef.current.matrix.decompose(pos, rot, scl);
			scl.multiplyScalar(1.03);
			highlightMeshRef.current.matrix.compose(pos, rot, scl);

			highlightMeshRef.current.visible = true;
		} else {
			highlightMeshRef.current.visible = false;
		}
	};

	return {
		highlightMeshRef,
		setHighlightMatrix,
	};
}